import { useCallback } from 'react';
import {
  rankWith,
  ControlProps,
  isControl,
  resolveSchema,
} from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { TreeSelectPicker } from '../TreeSelectPicker';
import { useTreeLookupNodes } from '@/lib/treeLookup';

/**
 * Tester: matches entity lookups declared with `lookup.presentation: tree` in the DSL.
 * Ranked above LookupRenderer (10) so tree-presented lookups win over the flat autocomplete.
 */
export const treeLookupRendererTester = rankWith(
  12,
  (uischema, schema, context) => {
    if (!isControl(uischema) || !schema) return false;

    // JSONForms passes the ROOT schema here — resolve the control's scope to the field
    // subschema, same as lookupRendererTester.
    const scope = (uischema as any).scope as string | undefined;
    const rootSchema = (context as any)?.rootSchema ?? schema;
    let fieldSchema: any = schema;
    if (scope) {
      try {
        fieldSchema = resolveSchema(rootSchema, scope, rootSchema) ?? schema;
      } catch {
        fieldSchema = schema;
      }
    }

    const presentation =
      fieldSchema?.['x-lookup']?.presentation ??
      (uischema.options as any)?.lookup?.presentation;
    return presentation === 'tree';
  },
);

/**
 * Tree-presented entity lookup — renders the same TreeSelectPicker the HierarchyRef
 * renderer uses, fed a client-built adjacency tree from the lookup entity's list endpoint.
 */
const TreeLookupRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, required, errors, schema, uischema, visible, enabled } = props;

  const lookupConfig = {
    ...((schema as any)?.['x-lookup'] || {}),
    ...((uischema.options as any)?.lookup || {}),
  } as { entity?: string; module?: string };

  const nodes = useTreeLookupNodes(lookupConfig.module || '', lookupConfig.entity || '');

  const onChange = useCallback(
    (nodeId: string | null) => {
      handleChange(path, nodeId || undefined);
    },
    [handleChange, path],
  );

  if (!visible) return null;
  if (!lookupConfig.entity) return null;

  return (
    <TreeSelectPicker
      nodes={nodes}
      value={data || null}
      onChange={onChange}
      label={label || 'Select'}
      disabled={!enabled || uischema.options?.readOnly === true}
      required={required}
      error={errors}
      size="small"
    />
  );
};

export const TreeLookupRenderer = withJsonFormsControlProps(TreeLookupRendererComponent);

export const treeLookupRendererEntry = {
  tester: treeLookupRendererTester,
  renderer: TreeLookupRenderer,
};
